import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PpcSalesClient, type PpcSalesDTO } from "../../api/manualInvoice";
import { currencyFromId } from "../../api/part";

/**
 * Manual invoices → PPC: read-only advertising sales rows from imported
 * Google/Facebook/Amazon/Wayfair/Overstock invoices. Every invoice column
 * reads through the nested invoice object, as the Vaadin grid did.
 */
export function PpcSalesGrid({ toolbar }: { toolbar: ReactNode }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new PpcSalesClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["manual-invoice", "ppc"],
    queryFn: () => client.findAll(),
    staleTime: 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<PpcSalesDTO>();
    return [
      c.text("supplier", (row) => row.invoice?.invoice_supplier ?? "", {
        headerKey: "column.invoice-supplier",
        filter: "select",
        priority: 1,
      }),
      c.text("invoice_no", (row) => row.invoice?.invoice_no ?? "", { headerKey: "column.invoice-no", priority: 2 }),
      c.text("invoice_date", (row) => row.invoice?.invoice_date ?? "", {
        headerKey: "column.invoice-date",
        filter: "daterange",
        priority: 3,
      }),
      c.text("currency", (row) => currencyFromId(row.invoice?.invoice_currency), {
        headerKey: "column.invoice-currency",
        filter: "select",
      }),
      c.custom(
        "client",
        (row) => `${row.client_name ?? ""} ${row.client_no ?? ""}`,
        (row) => (
          <span>
            {row.client_name}
            <span className="ml-1 text-[11px] text-ink-faint">{row.client_no}</span>
          </span>
        ),
        { headerKey: "column.client", priority: 2 },
      ),
      c.number("subtotal", (row) => row.invoice?.subtotal ?? 0, { headerKey: "column.subtotal", priority: 3 }),
      c.number("tax_1", (row) => row.invoice?.tax_1 ?? 0, { headerKey: "column.tax-1", defaultVisible: false }),
      c.number("tax_2", (row) => row.invoice?.tax_2 ?? 0, { headerKey: "column.tax-2", defaultVisible: false }),
      c.number("sales", (row) => row.sales ?? 0, { headerKey: "column.sales", priority: 3 }),
    ];
  }, []);

  return (
    <>
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        toolbarExtra={toolbar}
        loading={isLoading}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
